// src/pages/hr/IntentionReview.tsx
import React, { useState, useEffect } from 'react';
import {
  CheckCircle, AlertTriangle, Clock, Search,
  X, FileText, MessageSquareText, MessageCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import { intentionPeriodsService } from '../../services/intentionPeriodsService';
import { intentionsService } from '../../services/intentionsService';
import { IntentionPeriod, IntentionPeriodStats, HRReviewStatus } from '../../types/intentionPeriods';
import { TeachingIntention } from '../../types/intentions';
import IntentionDetail from '../../components/intentions/IntentionDetail';
import { displayLearningArea } from '../../constants/learningAreas';
import { getIntentionStatusMeta } from '../../utils/intentionStats';
import EmptyState from '../../components/ui/EmptyState';
import FteReconciliationPanel from '../../components/intentions/FteReconciliationPanel';

const IntentionReview: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState<IntentionPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [periodStats, setPeriodStats] = useState<IntentionPeriodStats | null>(null);
  const [intentions, setIntentions] = useState<TeachingIntention[]>([]);
  const [filteredIntentions, setFilteredIntentions] = useState<TeachingIntention[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<HRReviewStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [conversationOnly, setConversationOnly] = useState(false);

  // Selected for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Review modal
  const [reviewingIntention, setReviewingIntention] = useState<TeachingIntention | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriodId) {
      loadIntentions();
    }
  }, [selectedPeriodId]);

  useEffect(() => {
    filterIntentions();
  }, [intentions, statusFilter, searchTerm, conversationOnly]);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      // Load closed and open periods (for review)
      const allPeriods = await intentionPeriodsService.getAllPeriods();
      const reviewablePeriods = allPeriods.filter(p => p.status === 'closed' || p.status === 'open');
      setPeriods(reviewablePeriods);

      // Auto-select most recent closed period
      const closedPeriod = reviewablePeriods.find(p => p.status === 'closed');
      if (closedPeriod) {
        setSelectedPeriodId(closedPeriod.id);
      } else if (reviewablePeriods.length > 0) {
        setSelectedPeriodId(reviewablePeriods[0].id);
      }
    } catch (err) {
      console.error('Error loading periods:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadIntentions = async () => {
    try {
      setLoading(true);
      const [intentionsList, stats] = await Promise.all([
        intentionsService.getIntentionsByPeriod(selectedPeriodId),
        intentionPeriodsService.getPeriodStats(selectedPeriodId)
      ]);

      setIntentions(intentionsList);
      setPeriodStats(stats);
    } catch (err) {
      console.error('Error loading intentions:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterIntentions = () => {
    let filtered = [...intentions];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }

    // Conversation-requested filter: teacher selected "I'd like to discuss my
    // options" for FTE or day-off preference (captured as hrDiscussFlag).
    if (conversationOnly) {
      filtered = filtered.filter(i => i.hrDiscussFlag === true);
    }

    // Search filter. Matches the person AND what they asked to teach: the
    // placeholder has always promised learning-area search, but the filter only
    // ever looked at staff name/code/department, so searching "Media" returned
    // nothing. Subjects are searched too — both the preference form's subject
    // list and the RUAH list — plus the legacy `teachingPreferences` rows, which
    // are the only preferences imported intentions carry.
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(i => {
        const haystack = [
          i.staffName,
          i.staffId,
          i.department,
          ...(i.secondaryDepartments || []),
          ...(i.preferences?.curriculumAreas || []).map(a => a.areaName),
          ...(i.preferences?.subjects || []).map(s => s.subjectName),
          ...(i.preferences?.ruahSubjects || []).map(s => s.subjectName),
          ...(i.teachingPreferences || []).flatMap(p => [p.learningArea, p.specificSubject]),
          ...(i.comfortableTeachingAreas || []),
        ];
        return haystack.some(field => (field || '').toLowerCase().includes(search));
      });
    }

    setFilteredIntentions(filtered);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredIntentions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredIntentions.map(i => i.id)));
    }
  };

  const handleSelectIntention = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0 || !user?.id) return;

    if (!(await confirm({
      title: 'Approve intentions?',
      message: `Approve ${selectedIds.size} intention(s)?`,
      confirmLabel: 'Approve',
      variant: 'primary',
    }))) return;

    try {
      setSubmitting(true);
      const ids = Array.from(selectedIds);
      await intentionsService.bulkApproveIntentions(ids, user.id);
      setSelectedIds(new Set());
      await loadIntentions();
    } catch (err) {
      console.error('Error bulk approving:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (intention: TeachingIntention) => {
    if (!user?.id) return;

    try {
      setSubmitting(true);
      await intentionsService.approveIntention(intention.id, user.id, reviewNotes || undefined);
      setReviewingIntention(null);
      setReviewNotes('');
      await loadIntentions();
    } catch (err) {
      console.error('Error approving:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFlag = async (intention: TeachingIntention) => {
    if (!user?.id || !reviewNotes) {
      addToast('Please provide notes explaining why this is being flagged', 'warning');
      return;
    }

    try {
      setSubmitting(true);
      await intentionsService.flagIntention(intention.id, user.id, reviewNotes);
      setReviewingIntention(null);
      setReviewNotes('');
      await loadIntentions();
    } catch (err) {
      console.error('Error flagging:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const statusToneIcon: Record<string, React.ReactNode> = {
    neutral: <FileText className="w-3 h-3" />,
    warn: <Clock className="w-3 h-3" />,
    ok: <CheckCircle className="w-3 h-3" />,
    danger: <AlertTriangle className="w-3 h-3" />,
    info: <FileText className="w-3 h-3" />,
  };

  const getStatusBadge = (status: string) => {
    // Canonical label + colours shared with /intentions and the Discuss queue.
    const meta = getIntentionStatusMeta(status);
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.bg} ${meta.text}`}>
        {statusToneIcon[meta.tone]}
        {meta.label}
      </span>
    );
  };

  const selectedPeriod = periods.find(p => p.id === selectedPeriodId);

  if (loading && periods.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-ink-200 rounded w-1/4"></div>
          <div className="h-64 bg-ink-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink-900">Review Teaching Intentions</h1>
        <p className="text-ink-500 mt-1">
          Review and approve staff teaching intention submissions
        </p>
      </div>

      {/* Period Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink-700 mb-2">
          Select Submission Period
        </label>
        <select
          value={selectedPeriodId}
          onChange={(e) => setSelectedPeriodId(e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
        >
          <option value="">Select a period...</option>
          {periods.map((period) => (
            <option key={period.id} value={period.id}>
              {period.name} ({period.status})
            </option>
          ))}
        </select>
      </div>

      {selectedPeriod && (
        <>
          {/* Stats */}
          {periodStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-ink-200 p-4">
                <p className="text-2xl font-bold text-ink-900 font-mono tabular-nums">{periodStats.totalSubmissions}</p>
                <p className="text-xs text-ink-500">Total Submissions</p>
              </div>
              <div className="bg-white rounded-lg border border-ink-200 p-4">
                <p className="text-2xl font-bold text-warn-fg font-mono tabular-nums">{periodStats.pendingReviewCount}</p>
                <p className="text-xs text-ink-500">Pending Review</p>
              </div>
              <div className="bg-white rounded-lg border border-ink-200 p-4">
                <p className="text-2xl font-bold text-ok-fg font-mono tabular-nums">{periodStats.hrApprovedCount}</p>
                <p className="text-xs text-ink-500">Approved</p>
              </div>
              <div className="bg-white rounded-lg border border-ink-200 p-4">
                <p className="text-2xl font-bold text-danger-fg font-mono tabular-nums">{periodStats.hrFlaggedCount}</p>
                <p className="text-xs text-ink-500">Flagged</p>
              </div>
              <div className="bg-white rounded-lg border border-ink-200 p-4">
                <p className="text-2xl font-bold text-navy-600 font-mono tabular-nums">{periodStats.submissionRate}%</p>
                <p className="text-xs text-ink-500">Response Rate</p>
              </div>
            </div>
          )}

          {/* Staff whose recorded FTE still disagrees with what they asked for.
              Nothing applies these automatically — see issue #226. Renders nothing
              when everything already agrees. */}
          <FteReconciliationPanel
            intentions={intentions}
            year={periods.find(p => p.id === selectedPeriodId)?.academicYear ?? new Date().getFullYear()}
          />

          {/* Filters */}
          <div className="bg-white rounded-lg border border-ink-200 p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, code, learning area, or subject..."
                  className="w-full pl-10 pr-4 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as HRReviewStatus | 'all')}
                className="px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
              >
                <option value="all">All Statuses</option>
                <option value="submitted">Pending Review</option>
                <option value="hr_approved">Approved</option>
                <option value="hr_flagged">Flagged</option>
                <option value="draft">Draft</option>
              </select>

              {/* Conversation-requested Filter */}
              <label
                className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer select-none ${
                  conversationOnly
                    ? 'bg-navy-50 border-navy-300 text-navy-800'
                    : 'border-ink-300 text-ink-700 hover:bg-ink-50'
                }`}
                title='Only show submissions where the teacher selected "I&apos;d like to discuss my options"'
              >
                <input
                  type="checkbox"
                  checked={conversationOnly}
                  onChange={(e) => setConversationOnly(e.target.checked)}
                  className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                />
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Conversation requested</span>
              </label>

              {/* Bulk Actions */}
              {selectedIds.size > 0 && (
                <button
                  onClick={handleBulkApprove}
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 bg-ok-solid text-white rounded-lg hover:bg-ok-solid disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve Selected ({selectedIds.size})
                </button>
              )}
            </div>
          </div>

          {/* Intentions List */}
          <div className="bg-white rounded-lg border border-ink-200">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredIntentions.length && filteredIntentions.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Staff</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">FTE</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Learning Areas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {filteredIntentions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-4">
                      <EmptyState
                        icon={<FileText className="h-6 w-6" />}
                        title="No submissions found"
                        description="Try adjusting your search or filter criteria."
                      />
                    </td>
                  </tr>
                ) : (
                  filteredIntentions.map((intention) => (
                    <tr key={intention.id} className="hover:bg-ink-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(intention.id)}
                          onChange={() => handleSelectIntention(intention.id)}
                          className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-ink-900 flex items-center gap-1.5">
                            {intention.staffName}
                            {intention.hrDiscussFlag && (
                              <span title="Requested a conversation" className="inline-flex">
                                <MessageCircle
                                  className="h-4 w-4 text-navy-500 flex-shrink-0"
                                  aria-label="Requested a conversation"
                                />
                              </span>
                            )}
                            {!!intention.additionalComments?.trim() && (
                              <span title="Has additional comments" className="inline-flex">
                                <MessageSquareText
                                  className="h-4 w-4 text-navy-500 flex-shrink-0"
                                  aria-label="Has additional comments"
                                />
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-ink-500">{intention.staffId} • {displayLearningArea(intention.department)}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-ink-900 font-mono tabular-nums">
                        {intention.availability?.fte ? `${Math.round(intention.availability.fte * 100)}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-ink-500">
                        {intention.preferences?.curriculumAreas?.slice(0, 3).map(a => a.areaName).join(', ') || '-'}
                        {(intention.preferences?.curriculumAreas?.length || 0) > 3 && '...'}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(intention.status)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            setReviewingIntention(intention);
                            setReviewNotes(intention.hrNotes || '');
                          }}
                          className="text-navy-600 hover:text-navy-800 text-sm font-medium"
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Review Modal */}
      {reviewingIntention && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-card-title text-ink-900">
                Review Submission
              </h3>
              <button
                onClick={() => {
                  setReviewingIntention(null);
                  setReviewNotes('');
                }}
                className="p-1 text-ink-400 hover:text-ink-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <IntentionDetail
                intention={reviewingIntention}
                onSaved={() => { loadIntentions(); }}
              />

              {/* Review Notes */}
              <div>
                <label className="block font-medium text-ink-900 mb-2">
                  Review Notes
                  {reviewingIntention.status !== 'hr_flagged' && (
                    <span className="text-ink-500 font-normal"> (required if flagging)</span>
                  )}
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                  placeholder="Add notes about this submission..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-ink-50 rounded-b-lg">
              <button
                onClick={() => {
                  setReviewingIntention(null);
                  setReviewNotes('');
                }}
                className="px-4 py-2 text-ink-700 hover:text-ink-900"
              >
                Cancel
              </button>
              <button
                onClick={() => handleFlag(reviewingIntention)}
                disabled={submitting || !reviewNotes}
                className="flex items-center gap-2 px-4 py-2 bg-danger-solid text-white rounded-lg hover:bg-danger-solid disabled:opacity-50"
              >
                <AlertTriangle className="w-4 h-4" />
                Flag for Discussion
              </button>
              <button
                onClick={() => handleApprove(reviewingIntention)}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 bg-ok-solid text-white rounded-lg hover:bg-ok-solid disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntentionReview;
